/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { classNames } from '../utils/UNSAFE_classNames';
import { skeletonStyles } from './themes/ListStyles.css';

type Props = {
  /**
   * Allows to pass and access the properties from child elements
   **/
  children?: (index: number) => ComponentChildren;
  /**
   * Allows to specify number of skeletons to be rendered on initial load
   **/
  minimumCount?: number;
};

/**
 * Allows to specify the time delay for rendering the component
 **/
const timerValue = 50;

/**
 * SkeletonContainer renders 'minimumCount' number of skeletons
 * of the variant specified from its child element - Skeleton's prop after
 * 'timerValue' ms delay
 **/
export function SkeletonContainer({ children, minimumCount = 1 }: Props) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setIsVisible(true);
    }, timerValue);
  }, []);

  const containerClasses = classNames([skeletonStyles.container]);
  return isVisible && children ? (
    <div class={containerClasses} role="presentation">
      {[...Array(minimumCount)].map((_element, index) => children(index))}
    </div>
  ) : null;
}
